import { DynamicFormFieldFragment } from 'generated/resources/graphql'
import { camelCase } from 'lodash'

import i18n from 'modules/core/lib/i18n'

import { DynamicFormErrors, DynamicFormValues } from 'modules/dynamic_forms/types'

/**
 * Validates dynamic form values against their schema field definitions.
 *
 * Performs client-side generic validation:
 * - Required field checks
 * - Basic type format checks (email)
 * - Options inclusion checks for enum fields
 *
 * Domain-specific validation remains the backend's responsibility.
 */
export function validateDynamicFormValues(
  fields: ReadonlyArray<DynamicFormFieldFragment>,
  values: DynamicFormValues
): DynamicFormErrors {
  const errors: DynamicFormErrors = {}

  for (const field of fields) {
    if (field.hidden) continue

    const fieldName = camelCase(field.fieldName)
    const value = values[fieldName]

    // Required check
    if (field.required && (value === undefined || value === '' || value === null)) {
      errors[fieldName] = i18n.t('validations.required')
      continue
    }

    // Skip further validation if value is empty and not required
    if (value === undefined || value === '' || value === null) continue

    const stringValue = typeof value === 'string' ? value : String(value)

    // Email format check
    if (field.fieldType === 'email' && stringValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(stringValue)) {
        errors[fieldName] = i18n.t('validations.email')
      }
    }

    // Enum options inclusion check
    if (
      (field.fieldType === 'enum' || field.fieldType === 'single_select') &&
      field.allowedValues?.length &&
      stringValue
    ) {
      const validKeys = field.allowedValues.map((av) => av.key)
      if (!validKeys.includes(stringValue)) {
        errors[fieldName] = i18n.t('validations.inclusion')
      }
    }
  }

  return errors
}

/**
 * Maps backend field-level errors (keyed by snake_case field name)
 * to the camelCase format used by the frontend form state.
 */
function mapBackendErrors(backendErrors: Record<string, string | string[]>): DynamicFormErrors {
  const mapped: DynamicFormErrors = {}

  for (const [key, value] of Object.entries(backendErrors)) {
    const fieldName = camelCase(key)
    mapped[fieldName] = Array.isArray(value) ? value[0] : value
  }

  return mapped
}
